import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useTagApi custom hook for tag API operations.
 * - Provides CRUD operations for tags using Apollo Client and GraphQL.
 * - Handles cache updates for tag queries and mutations.
 * - Exposes helpers for reading/writing/updating tags in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting tags.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const TAG_QUERIES = {
  // Query to fetch all tags
  GET_TAGS: gql`
    query GetTags {
      GetTags {
        tag_id
        tag_name
        tag_description
        tag_type   
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Query to fetch a tag by ID
  GET_TAG: gql`
    query GetTagById($tag_id: ID!) {
      GetTagById(tag_id: $tag_id) {
        tag_id
        tag_name
        tag_description
        tag_type
        is_active
        created_on
        updated_on
      }
    }
  `,
  //Query to fetch tags based on tag type
  GET_TAG_BY_TYPE: gql`
    query GetTagByTagType($tag_type: String!) {
      GetTagByTagType(tag_type: $tag_type) {
        tag_id
        tag_name
        tag_description
        tag_type
        is_active
        created_on
        updated_on
      }
    }
  `,
    
  // Mutation to add a new tag
  ADD_TAG: gql`
    mutation AddTag(
      $tag_name: String!
      $tag_description: String!
      $tag_type: String!
    ) {
      AddTag(
        tag_name: $tag_name
        tag_description: $tag_description
        tag_type: $tag_type
      ) {
        tag_id
        tag_name
        tag_description
        tag_type   
        is_active
        created_on
        updated_on
      }
    }
  `,


  // Mutation to update an existing tag
  UPDATE_TAG: gql`
    mutation UpdateTag(
      $tag_id: ID!
      $tag_name: String
      $tag_description: String
      $tag_type: String
    ) {
      UpdateTag(
        tag_id: $tag_id
        tag_name: $tag_name
        tag_description: $tag_description
        tag_type: $tag_type
      ) {
        tag_id
        tag_name
        tag_description
        tag_type   
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a tag
  DELETE_TAG: gql`
    mutation DeleteTag($tag_id: ID!) {
      DeleteTag(tag_id: $tag_id) {
        tag_id
        tag_name
        tag_description
        tag_type   
        is_active
        created_on
        updated_on
      }
    }
  `,
};

/**
 * Cache operations for tags.
 * - readTags: Reads tags from Apollo cache.
 * - writeTags: Writes tags to Apollo cache.
 * - updateTagInCache: Updates a tag in Apollo cache.
 */
const cacheOperations = {
  readTags: () => {
    try {
      const { GetTags = [] } =
        client.readQuery({
          query: TAG_QUERIES.GET_TAGS,
        }) || {};
      return GetTags;
    } catch {
      return [];
    }
  },

  writeTags: (tags) => {
    if (!tags) return;
    client.writeQuery({
      query: TAG_QUERIES.GET_TAGS,
      data: { GetTags: tags },
    });
  },

  updateTagInCache: (updatedTag) => {
    const existingTags = cacheOperations.readTags();
    const updatedTags = existingTags.map((tag) =>
      tag.tag_id === updatedTag.tag_id
        ? updatedTag
        : tag
    );
    cacheOperations.writeTags(updatedTags);
  },
};

export const useTagApi = () => {
  // Mutation for adding a tag
  const [addTagMutation] = useMutation(TAG_QUERIES.ADD_TAG, {
    update(cache, { data: { AddTag } }) {
      try {
        const existingData = cache.readQuery({
          query: TAG_QUERIES.GET_TAGS,
        });
        if (existingData) {
          cache.writeQuery({
            query: TAG_QUERIES.GET_TAGS,
            data: {
              GetTags: [AddTag, ...existingData.GetTags],
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after adding tag:", error);
      }
    },
  });

  // Mutation for updating a tag
  const [updateTagMutation] = useMutation(TAG_QUERIES.UPDATE_TAG, {
    update(cache, { data: { UpdateTag } }) {
      try {
        const existingData = cache.readQuery({
          query: TAG_QUERIES.GET_TAGS,
        });
        console.log("The existing data in Update Tag Mutation hook is", existingData);
        if (existingData) {
          const newData = existingData.GetTags.map((tag) =>
            tag.tag_id === UpdateTag.tag_id ? UpdateTag : tag
          );
          console.log("The newData in Update Tag Mutation hook is", newData);
          cache.writeQuery({
            query: TAG_QUERIES.GET_TAGS,
            data: { GetTags: newData },
          });
          console.log("The After updating cache in Update Tag Mutation hook is", newData);
        }
      } catch (error) {
        console.error("Error updating cache after updating tag:", error);
      }
    },
  });

  // Mutation for deleting a tag
  const [deleteTagMutation] = useMutation(TAG_QUERIES.DELETE_TAG, {
    update(cache, { data: { DeleteTag } }) {
      try {
        const existingData = cache.readQuery({
          query: TAG_QUERIES.GET_TAGS,
        });
        if (existingData) {
          cache.writeQuery({
            query: TAG_QUERIES.GET_TAGS,
            data: {
              GetTags: existingData.GetTags.filter(
                (tag) => tag.tag_id !== DeleteTag.tag_id
              ),
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after deleting tag:", error);
      }
    },
  });

  /**
   * Fetches all tags from the backend and updates the cache.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getTags = async () => {
    try {
      const { data } = await client.query({
        query: TAG_QUERIES.GET_TAGS,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });
      return { success: true, data: data.GetTags };
    } catch (error) {
      console.error("Error fetching tags:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a tag by its ID.
   * @param {string|number} tag_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getTagById = async (tag_id) => {
    try {
      const { data } = await client.query({
        query: TAG_QUERIES.GET_TAG,
        variables: { tag_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetTagById };
    } catch (error) {
      console.error("Error fetching tag:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches tags by their type.
   * @param {string} tag_type
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getTagsByType = async (tag_type) => {
    try {
      const { data } = await client.query({
        query: TAG_QUERIES.GET_TAG_BY_TYPE,
        variables: { tag_type },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetTagByTagType };
    } catch (error) {
      console.error("Error fetching tags by type:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new tag.
   * @param {Object} formData - { tag_name, tag_description, is_active }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createTag = async (formData) => {
    try {
      const { data } = await addTagMutation({
        variables: {
          tag_name: formData.tag_name,
          tag_description: formData.tag_description,
          tag_type: formData.tag_type,
          is_active: formData.is_active
        },
      });
      return { success: true, data: data.AddTag };
    } catch (error) {
      console.error("Error creating tag:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates an existing tag.
   * @param {string|number} tagId
   * @param {Object} formData - { tag_name, tag_description, is_active }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateTag = async (tagId, formData) => {
    try {
      const { data } = await updateTagMutation({
        variables: {
          tag_id: tagId,
          tag_name: formData.tag_name,
          tag_description: formData.tag_description,
          tag_type: formData.tag_type,
          is_active: formData.is_active
        },
      });
      return { success: true, data: data.UpdateTag };
    } catch (error) {
      console.error("Error updating tag:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Deletes a tag by its ID.
   * @param {string|number} tag_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteTag = async (tag_id) => {
    try {
      const { data } = await deleteTagMutation({
        variables: { tag_id },
      });
      return { success: true, data: data.DeleteTag };
    } catch (error) {
      console.error("Error deleting tag:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getTags,
    getTagById,
    createTag,
    updateTag,
    deleteTag,
    cacheOperations,
    getTagsByType,
  };
};
